/**
 * EN / ES translations for the /dashboards-reporting service page.
 * Imported and merged into the main translations object.
 */
export const dashboardsReportingTranslations = {
  en: {
    dashboardsReporting: {
      heroEyebrow: "Kranbos Dashboards & Reporting",
      heroTitle: "See What Is Happening Across Your Business",
      heroSubtitle:
        "Kranbos brings information from your existing systems into clear dashboards and reports—so you can understand workload, performance, bottlenecks, and the items that need attention.",
      heroPrimaryCta: "Request a Reporting Audit",
      heroSecondaryCta: "See What We Track",

      mockLabel: "Illustrative example dashboard",
      mockDisclaimer:
        "Illustrative example only. The values shown are placeholders and do not represent a Kranbos customer.",
      mockFilterLabel: "Location",
      mockFilterValue: "All locations",
      mockPeriodLabel: "Period",
      mockPeriodValue: "This week",
      mockMetric1: "New Requests",
      mockMetric2: "Estimates Pending",
      mockMetric3: "Active Jobs",
      mockMetric4: "Work Completed",
      mockMetric5: "Overdue Follow-Ups",
      mockMetric6: "Outstanding Items",
      mockChartTitle: "Weekly activity",
      mockChartCaption: "New work received per day (example values)",
      mockDay1: "Mon",
      mockDay2: "Tue",
      mockDay3: "Wed",
      mockDay4: "Thu",
      mockDay5: "Fri",
      mockStatusTitle: "Work by status",
      mockAttentionTitle: "Needs attention",
      mockAttention1: "3 requests not assigned",
      mockAttention2: "4 estimates waiting on a response",
      mockAttention3: "2 jobs missing required information",

      statusNew: "New",
      statusWaiting: "Waiting for Information",
      statusEstimate: "Estimate Pending",
      statusScheduled: "Scheduled",
      statusInProgress: "In Progress",
      statusComplete: "Complete",
      statusOnHold: "On Hold",

      problemEyebrow: "Scattered information",
      problemTitle: "The answers exist. They are just spread across too many places.",
      problemText1:
        "Most businesses already have useful information inside their CRM, scheduling platform, email, accounting software, spreadsheets, forms, project tools, and industry-specific systems.",
      problemText2:
        "The challenge is turning that information into a clear and consistent view of the business.",
      problemText3:
        "Owners and managers often have to open several platforms, request updates from employees, combine spreadsheets, and manually rebuild the same reports every week. By the time the report is complete, the information may already be outdated.",
      problemText4:
        "Kranbos helps organize the available data into reporting that is easier to understand, maintain, and act on.",
      currentTitle: "Current reporting process",
      current1: "Data lives in multiple platforms",
      current2: "Reports are rebuilt manually",
      current3: "Metrics are calculated differently by different people",
      current4: "Managers ask employees for status updates",
      current5: "Important exceptions are buried in spreadsheets",
      current6: "Reports are already outdated when completed",
      current7: "Teams spend time preparing information instead of using it",
      improvedTitle: "Improved reporting process",
      improved1: "Important information is brought into one view",
      improved2: "Metrics follow consistent definitions",
      improved3: "Reports update on an appropriate schedule",
      improved4: "Managers can filter by team, location, status, or period",
      improved5: "Overdue and incomplete items are easier to identify",
      improved6: "Different roles see the information relevant to them",
      improved7: "Reports support clearer follow-up and decision-making",
      problemNote:
        "Not every report can update in real time. Some data sources only support scheduled refreshes or a controlled manual review.",

      capabilitiesEyebrow: "What we can build",
      capabilitiesTitle: "Reporting designed around the questions your business needs answered",
      capabilitiesIntro:
        "Kranbos begins with the decisions, responsibilities, and recurring questions behind the report. We then determine what information is available and how it can be presented clearly.",
      cap1Title: "Operational dashboards",
      cap1Text:
        "Monitor requests, assignments, active work, incomplete steps, overdue items, and daily activity across the business.",
      cap2Title: "Performance reporting",
      cap2Text:
        "Track meaningful measures by team, employee, location, service line, customer type, or reporting period.",
      cap3Title: "Pipeline and status reporting",
      cap3Text:
        "See how leads, estimates, projects, jobs, applications, or other records move through each stage of the process.",
      cap4Title: "Exception and attention views",
      cap4Text:
        "Surface missing information, stalled work, overdue follow-ups, failed processes, and items that need human review.",
      cap5Title: "Scheduled reports",
      cap5Text:
        "Deliver recurring summaries to owners, managers, or teams without requiring someone to rebuild the report manually.",
      cap6Title: "Custom filters and drill-down views",
      cap6Text:
        "Allow users to move from a high-level summary into the records, locations, teams, or time periods behind the numbers.",

      questionsEyebrow: "From data to decisions",
      questionsTitle: "A useful dashboard should help you decide what to do next",
      question1: "How much new work entered the business this week?",
      question2: "Which requests have not been assigned?",
      question3: "Which estimates are still waiting for a response?",
      question4: "Where is work becoming delayed?",
      question5: "Which jobs or projects are missing information?",
      question6: "What needs attention today?",
      question7: "How much work is assigned to each employee or contractor?",
      question8: "Which locations or service lines have the highest workload?",
      question9: "Which follow-ups are overdue?",
      question10: "What was completed during the reporting period?",
      question11: "Which records are inactive or stalled?",
      question12: "Where do the numbers in the report come from?",
      questionsClosing:
        "The final dashboard should be organized around the questions that matter to the people using it—not around every field that happens to exist in the source systems.",

      sourcesEyebrow: "Work with your existing data",
      sourcesTitle: "Bring together the information your business already creates",
      sourcesText1:
        "Dashboards are only useful when the underlying information is available, understandable, and reliable.",
      sourcesText2:
        "Kranbos evaluates where your business data currently lives, how often it changes, and how it can be collected without disrupting the systems your team already uses.",
      source1: "CRM and customer management platforms",
      source2: "Scheduling software",
      source3: "Project and task management tools",
      source4: "Microsoft 365 and Outlook",
      source5: "Google Workspace",
      source6: "Accounting and invoicing platforms",
      source7: "Forms and intake systems",
      source8: "Spreadsheets and shared files",
      source9: "Document-management platforms",
      source10: "Call-tracking and communication tools",
      source11: "Industry-specific software",
      source12: "Internal databases",
      source13: "Approved third-party APIs",
      sourcesNote1:
        "Depending on the source, information may be synchronized automatically, retrieved on a schedule, imported from an approved file, or reviewed through a controlled manual step.",
      sourcesNote2:
        "When a platform does not provide reliable access to the required information, Kranbos identifies the limitation clearly and recommends the most practical alternative.",

      exampleEyebrow: "Example reporting solution",
      exampleTitle: "One operational view for a growing service business",
      exampleIntro1:
        "A growing service business may use separate platforms for incoming leads, estimates, scheduling, job management, employees, contractors, invoicing, and customer communication. Managers may understand each individual system but still struggle to see the complete operational picture.",
      exampleIntro2:
        "Kranbos could create a dashboard that brings the most important information into one organized view. The screen below is an illustrative example, not a real client case study.",
      exCard1Title: "New Requests",
      exCard1Text: "See how much new work entered the business during the selected period.",
      exCard2Title: "Estimates Pending",
      exCard2Text:
        "Track estimates that are waiting for preparation, approval, delivery, or customer response.",
      exCard3Title: "Scheduled Work",
      exCard3Text: "See upcoming work by date, team, location, or assigned employee.",
      exCard4Title: "Active Jobs",
      exCard4Text:
        "Monitor work currently in progress and identify jobs that have remained active longer than expected.",
      exCard5Title: "Completed Work",
      exCard5Text:
        "Review completed jobs, projects, or service requests during the selected period.",
      exCard6Title: "Needs Attention",
      exCard6Text:
        "Surface overdue follow-ups, missing documents, unassigned requests, stalled work, or data errors.",
      exStatusTitle: "Work by status",
      exWorkloadTitle: "Workload by team or employee",
      exWorkloadText:
        "Compare assigned work and identify uneven distribution or overloaded team members.",
      exActivityTitle: "Activity over time",
      exActivityText: "New, scheduled, completed, or closed work across days, weeks, or months.",
      exFollowUpsTitle: "Outstanding follow-ups",
      exFollowUpsText: "Records that require customer, employee, contractor, or manager action.",
      exFilterTitle: "Location or service-line filter",
      exFilterText:
        "Focus the dashboard on a specific office, territory, team, or type of service.",
      exActivityFeedTitle: "Recent activity",
      exActivityFeedText:
        "Important status changes, completed work, new assignments, and exceptions.",
      exFollowUp1: "Estimate awaiting customer response",
      exFollowUp2: "Missing site photos before scheduling",
      exFollowUp3: "Unassigned request from the web form",
      exFollowUp4: "Job on hold pending customer approval",
      exActivity1: "Job moved to In Progress",
      exActivity2: "Estimate delivered to customer",
      exActivity3: "New request received from intake form",
      exActivity4: "Work marked complete by assigned team",
      exTeam1: "Team A",
      exTeam2: "Team B",
      exTeam3: "Contractors",
      exTeam4: "Unassigned",
      exampleClosing:
        "The exact metrics, definitions, and refresh frequency depend on the systems and information available to the business.",

      rolesEyebrow: "Reporting by responsibility",
      rolesTitle: "Not everyone needs the same dashboard",
      rolesText1:
        "Owners, managers, employees, and contractors often need different levels of information.",
      rolesText2:
        "Kranbos can organize dashboards and reports around the responsibilities of each role instead of showing every user the same overwhelming screen.",
      role1Title: "Owner overview",
      role1Text:
        "See business-wide activity, trends, workload, major exceptions, and the areas requiring management attention.",
      role2Title: "Operations manager",
      role2Text:
        "Monitor assignments, active work, deadlines, incomplete records, team capacity, and operational bottlenecks.",
      role3Title: "Team member",
      role3Text:
        "See assigned work, priorities, due dates, missing information, and the actions required next.",
      role4Title: "External contractor",
      role4Text:
        "View only approved assignments, relevant documents, due dates, and completion requirements.",
      rolesNote:
        "Access and visibility should be based on what each person needs to understand or complete—not simply on what data is available.",

      deliveryEyebrow: "Beyond the dashboard",
      deliveryTitle: "Put the right information in front of people at the right time",
      deliveryText1:
        "Some users need an interactive dashboard. Others may need a weekly summary, an exception report, a scheduled email, or a downloadable file.",
      deliveryText2:
        "Kranbos can help determine the most useful reporting format for each audience.",
      delivery1Title: "Interactive dashboards",
      delivery1Text: "Explore current activity using filters, status views, and drill-down details.",
      delivery2Title: "Weekly summaries",
      delivery2Text:
        "Provide owners and managers with a consistent overview of what changed during the week.",
      delivery3Title: "Daily attention reports",
      delivery3Text: "Highlight overdue, incomplete, failed, or unassigned items requiring action.",
      delivery4Title: "Management reports",
      delivery4Text:
        "Summarize performance, workload, progress, and operational concerns for a selected period.",
      delivery5Title: "Team reports",
      delivery5Text:
        "Give departments, locations, or employees a focused view of their responsibilities and results.",
      delivery6Title: "Downloadable reports",
      delivery6Text:
        "Create approved exports or files for meetings, reconciliation, analysis, or recordkeeping.",
      deliveryNote:
        "The available formats depend on the systems involved and the reporting approach chosen for your business.",

      qualityEyebrow: "Reports you can understand",
      qualityTitle: "Clear reporting starts with clear definitions",
      qualityText1: "Two reports can display the same label while calculating it differently.",
      qualityText2:
        "For example, “new customer,” “completed job,” “active request,” or “conversion” may mean different things to different employees or systems.",
      qualityText3:
        "Before building a dashboard, Kranbos helps define what the important metrics mean, where the source information comes from, and which records should be included.",
      quality1Title: "Metric definitions",
      quality1Text:
        "Document how each important number, status, or category should be calculated.",
      quality2Title: "Source visibility",
      quality2Text:
        "Make it clear which platform, file, or record provides the underlying information.",
      quality3Title: "Data validation",
      quality3Text:
        "Identify missing values, duplicates, mismatched records, and inconsistent status usage that affect reporting.",
      quality4Title: "Refresh expectations",
      quality4Text:
        "Clarify whether information updates immediately, on a schedule, after an import, or after a manual review.",
      qualityNote:
        "A dashboard should not create false confidence. If the available data is incomplete or inconsistent, the solution should make that limitation visible.",

      automationEyebrow: "Visibility + action",
      automationTitle: "The dashboard shows the problem. Automation can help move it forward.",
      automationText1: "Dashboards and workflow automation often work together.",
      automationText2:
        "The dashboard gives owners and employees visibility into what is happening. Automation can handle repetitive actions such as sending reminders, creating tasks, requesting missing information, updating records, or escalating overdue work.",
      automationColLeft: "Dashboards & Reporting",
      automationLeft1: "Show current status",
      automationLeft2: "Track activity and trends",
      automationLeft3: "Highlight bottlenecks",
      automationLeft4: "Surface exceptions",
      automationLeft5: "Compare teams or locations",
      automationLeft6: "Support management decisions",
      automationLeft7: "Provide scheduled summaries",
      automationColRight: "Workflow Automation",
      automationRight1: "Move information between systems",
      automationRight2: "Create tasks and records",
      automationRight3: "Send notifications",
      automationRight4: "Request missing information",
      automationRight5: "Update approved statuses",
      automationRight6: "Escalate overdue work",
      automationRight7: "Trigger next steps",
      automationClosing:
        "Kranbos can build reporting independently or combine it with automation when the business needs both visibility and action.",
      automationCta: "Explore Workflow Automation",

      toolsEyebrow: "Reporting inside the workflow",
      toolsTitle: "Sometimes the dashboard should be part of the tool your team uses",
      toolsText1: "A standalone dashboard is useful when people primarily need visibility.",
      toolsText2:
        "When employees also need to assign work, update records, approve requests, upload documents, or resolve exceptions, the reporting may be more useful inside a custom internal tool.",
      toolsColLeft: "A dashboard may be enough when:",
      toolsLeft1: "Users mainly need visibility",
      toolsLeft2: "Source systems already support the required actions",
      toolsLeft3: "Reports are used for monitoring and decision-making",
      toolsLeft4: "The business needs recurring summaries",
      toolsLeft5: "Employees can act in the original systems",
      toolsColRight: "An internal tool may be more appropriate when:",
      toolsRight1: "Users need to update or manage records",
      toolsRight2: "Work must be assigned or approved",
      toolsRight3: "Employees need one operational queue",
      toolsRight4: "Contractors need controlled task access",
      toolsRight5: "Exceptions must be resolved in the same interface",
      toolsRight6: "The existing systems do not provide an effective workspace",
      toolsClosing:
        "Kranbos recommends the smallest practical solution that solves the actual operational problem.",
      toolsCta: "Explore Internal Tools",

      processEyebrow: "How it works",
      processTitle: "Start with the decisions behind the report",
      process1Title: "Reporting discovery",
      process1Text:
        "We identify the people using the report, the questions they need answered, and the decisions the information should support.",
      process2Title: "Data-source review",
      process2Text:
        "We evaluate the systems, spreadsheets, files, fields, statuses, and records that contain the required information.",
      process3Title: "Metric and report definition",
      process3Text:
        "We define important measures, filters, calculations, refresh expectations, and reporting responsibilities.",
      process4Title: "Dashboard development",
      process4Text:
        "We build and test the dashboard, reporting logic, data connections, filters, access rules, and delivery methods.",
      process5Title: "Launch and refinement",
      process5Text:
        "We deploy the reporting solution, document its use, validate the results, and improve it based on real operational feedback.",

      outcomesEyebrow: "The result",
      outcomesTitle:
        "Less time gathering information. More clarity about what needs attention.",
      outcome1Title: "One clearer view",
      outcome1Text:
        "Bring the most important operational information into a more organized reporting experience.",
      outcome2Title: "More consistent reporting",
      outcome2Text:
        "Reduce differences in how employees calculate, categorize, and present the same information.",
      outcome3Title: "Faster issue detection",
      outcome3Text: "Make incomplete, overdue, stalled, or unusual activity easier to identify.",
      outcome4Title: "Better management visibility",
      outcome4Text:
        "Help owners and managers understand workload, progress, responsibilities, and bottlenecks.",

      relatedTitle: "Reliable reporting may require more than a dashboard",
      relatedIntro:
        "Kranbos can combine reporting, workflow automation, internal tools, and spreadsheet improvements into one practical operational solution.",
      relatedCurrentLabel: "You are here",
      related1Title: "Dashboards & Reporting",
      related1Text:
        "Turn operational information into clear dashboards, reports, and attention views.",
      related2Title: "Workflow Automation",
      related2Text: "Connect systems and automatically move information and actions forward.",
      related3Title: "Internal Tools",
      related3Text:
        "Give your team a focused interface for managing and completing operational work.",
      related4Title: "Spreadsheet Rescue",
      related4Text:
        "Improve, automate, or replace spreadsheet-based reporting that has become difficult to trust or maintain.",
      relatedLearnMore: "Learn more",

      finalCtaTitle: "Get a clearer view of how your business is operating",
      finalCtaText:
        "Tell us what you currently track, where the information lives, and what is difficult to understand. We will help determine the most practical way to improve your dashboards and reporting.",
      finalPrimaryCta: "Request a Reporting Audit",
      finalSecondaryCta: "Contact Us",
    },
  },
  es: {
    dashboardsReporting: {
      heroEyebrow: "Paneles y Reportes Kranbos",
      heroTitle: "Ve Lo Que Está Pasando en Todo Tu Negocio",
      heroSubtitle:
        "Kranbos reúne la información de tus sistemas actuales en paneles y reportes claros—para que entiendas la carga de trabajo, el desempeño, los cuellos de botella y los pendientes que requieren atención.",
      heroPrimaryCta: "Solicitar una Auditoría de Reportes",
      heroSecondaryCta: "Ver Qué Medimos",

      mockLabel: "Panel de ejemplo ilustrativo",
      mockDisclaimer:
        "Solo es un ejemplo ilustrativo. Los valores mostrados son de referencia y no representan a un cliente de Kranbos.",
      mockFilterLabel: "Ubicación",
      mockFilterValue: "Todas las ubicaciones",
      mockPeriodLabel: "Periodo",
      mockPeriodValue: "Esta semana",
      mockMetric1: "Nuevas Solicitudes",
      mockMetric2: "Presupuestos Pendientes",
      mockMetric3: "Trabajos Activos",
      mockMetric4: "Trabajo Completado",
      mockMetric5: "Seguimientos Vencidos",
      mockMetric6: "Pendientes Abiertos",
      mockChartTitle: "Actividad semanal",
      mockChartCaption: "Trabajo nuevo recibido por día (valores de ejemplo)",
      mockDay1: "Lun",
      mockDay2: "Mar",
      mockDay3: "Mié",
      mockDay4: "Jue",
      mockDay5: "Vie",
      mockStatusTitle: "Trabajo por estado",
      mockAttentionTitle: "Requiere atención",
      mockAttention1: "3 solicitudes sin asignar",
      mockAttention2: "4 presupuestos esperando respuesta",
      mockAttention3: "2 trabajos sin información requerida",

      statusNew: "Nuevo",
      statusWaiting: "Esperando Información",
      statusEstimate: "Presupuesto Pendiente",
      statusScheduled: "Agendado",
      statusInProgress: "En Progreso",
      statusComplete: "Completado",
      statusOnHold: "En Pausa",

      problemEyebrow: "Información dispersa",
      problemTitle: "Las respuestas existen. Solo están repartidas en demasiados lugares.",
      problemText1:
        "La mayoría de los negocios ya tienen información útil en su CRM, plataforma de agendamiento, correo, software de contabilidad, hojas de cálculo, formularios, herramientas de proyectos y sistemas propios de su industria.",
      problemText2:
        "El reto es convertir esa información en una vista clara y consistente del negocio.",
      problemText3:
        "Los dueños y gerentes suelen tener que abrir varias plataformas, pedir actualizaciones a los empleados, combinar hojas de cálculo y rehacer los mismos reportes cada semana. Cuando el reporte está listo, la información ya puede estar desactualizada.",
      problemText4:
        "Kranbos ayuda a organizar los datos disponibles en reportes más fáciles de entender, mantener y usar.",
      currentTitle: "Proceso de reportes actual",
      current1: "Los datos viven en varias plataformas",
      current2: "Los reportes se rehacen manualmente",
      current3: "Cada persona calcula las métricas de forma distinta",
      current4: "Los gerentes piden actualizaciones a los empleados",
      current5: "Las excepciones importantes quedan enterradas en hojas de cálculo",
      current6: "Los reportes ya están desactualizados cuando se terminan",
      current7: "Los equipos dedican tiempo a preparar información en vez de usarla",
      improvedTitle: "Proceso de reportes mejorado",
      improved1: "La información importante se reúne en una sola vista",
      improved2: "Las métricas siguen definiciones consistentes",
      improved3: "Los reportes se actualizan con una frecuencia adecuada",
      improved4: "Los gerentes pueden filtrar por equipo, ubicación, estado o periodo",
      improved5: "Los pendientes vencidos e incompletos son más fáciles de identificar",
      improved6: "Cada rol ve la información que le corresponde",
      improved7: "Los reportes facilitan el seguimiento y la toma de decisiones",
      problemNote:
        "No todos los reportes pueden actualizarse en tiempo real. Algunas fuentes de datos solo permiten actualizaciones programadas o una revisión manual controlada.",

      capabilitiesEyebrow: "Qué podemos construir",
      capabilitiesTitle:
        "Reportes diseñados alrededor de las preguntas que tu negocio necesita responder",
      capabilitiesIntro:
        "Kranbos empieza por las decisiones, responsabilidades y preguntas recurrentes detrás del reporte. Luego determinamos qué información está disponible y cómo puede presentarse con claridad.",
      cap1Title: "Paneles operativos",
      cap1Text:
        "Monitorea solicitudes, asignaciones, trabajo activo, pasos incompletos, pendientes vencidos y actividad diaria del negocio.",
      cap2Title: "Reportes de desempeño",
      cap2Text:
        "Mide indicadores relevantes por equipo, empleado, ubicación, línea de servicio, tipo de cliente o periodo.",
      cap3Title: "Reportes de embudo y estado",
      cap3Text:
        "Observa cómo avanzan los prospectos, presupuestos, proyectos, trabajos, solicitudes u otros registros por cada etapa del proceso.",
      cap4Title: "Vistas de excepciones y atención",
      cap4Text:
        "Muestra información faltante, trabajo detenido, seguimientos vencidos, procesos fallidos y casos que requieren revisión humana.",
      cap5Title: "Reportes programados",
      cap5Text:
        "Envía resúmenes recurrentes a dueños, gerentes o equipos sin que alguien tenga que rehacer el reporte manualmente.",
      cap6Title: "Filtros y vistas de detalle",
      cap6Text:
        "Permite pasar de un resumen general a los registros, ubicaciones, equipos o periodos detrás de los números.",

      questionsEyebrow: "De los datos a las decisiones",
      questionsTitle: "Un buen panel debería ayudarte a decidir qué hacer a continuación",
      question1: "¿Cuánto trabajo nuevo entró al negocio esta semana?",
      question2: "¿Qué solicitudes no han sido asignadas?",
      question3: "¿Qué presupuestos siguen esperando respuesta?",
      question4: "¿Dónde se está retrasando el trabajo?",
      question5: "¿A qué trabajos o proyectos les falta información?",
      question6: "¿Qué requiere atención hoy?",
      question7: "¿Cuánto trabajo tiene asignado cada empleado o contratista?",
      question8: "¿Qué ubicaciones o líneas de servicio tienen más carga?",
      question9: "¿Qué seguimientos están vencidos?",
      question10: "¿Qué se completó durante el periodo del reporte?",
      question11: "¿Qué registros están inactivos o detenidos?",
      question12: "¿De dónde vienen los números del reporte?",
      questionsClosing:
        "El panel final debe organizarse alrededor de las preguntas que importan a quienes lo usan—no alrededor de cada campo que exista en los sistemas de origen.",

      sourcesEyebrow: "Trabaja con tus datos actuales",
      sourcesTitle: "Reúne la información que tu negocio ya genera",
      sourcesText1:
        "Los paneles solo son útiles cuando la información de base está disponible, es comprensible y es confiable.",
      sourcesText2:
        "Kranbos evalúa dónde vive hoy la información de tu negocio, con qué frecuencia cambia y cómo puede recopilarse sin interrumpir los sistemas que tu equipo ya usa.",
      source1: "CRM y plataformas de gestión de clientes",
      source2: "Software de agendamiento",
      source3: "Herramientas de proyectos y tareas",
      source4: "Microsoft 365 y Outlook",
      source5: "Google Workspace",
      source6: "Plataformas de contabilidad y facturación",
      source7: "Formularios y sistemas de captación",
      source8: "Hojas de cálculo y archivos compartidos",
      source9: "Plataformas de gestión documental",
      source10: "Herramientas de seguimiento de llamadas y comunicación",
      source11: "Software específico de la industria",
      source12: "Bases de datos internas",
      source13: "APIs de terceros aprobadas",
      sourcesNote1:
        "Según la fuente, la información puede sincronizarse automáticamente, obtenerse de forma programada, importarse desde un archivo aprobado o revisarse mediante un paso manual controlado.",
      sourcesNote2:
        "Cuando una plataforma no ofrece acceso confiable a la información requerida, Kranbos identifica la limitación con claridad y recomienda la alternativa más práctica.",

      exampleEyebrow: "Solución de reportes de ejemplo",
      exampleTitle: "Una vista operativa para un negocio de servicios en crecimiento",
      exampleIntro1:
        "Un negocio de servicios en crecimiento puede usar plataformas separadas para prospectos, presupuestos, agendamiento, gestión de trabajos, empleados, contratistas, facturación y comunicación con clientes. Los gerentes pueden entender cada sistema por separado y aun así no ver el panorama operativo completo.",
      exampleIntro2:
        "Kranbos podría crear un panel que reúna la información más importante en una sola vista organizada. La pantalla siguiente es un ejemplo ilustrativo, no un caso real de cliente.",
      exCard1Title: "Nuevas Solicitudes",
      exCard1Text: "Ve cuánto trabajo nuevo entró al negocio durante el periodo seleccionado.",
      exCard2Title: "Presupuestos Pendientes",
      exCard2Text:
        "Da seguimiento a presupuestos que esperan preparación, aprobación, entrega o respuesta del cliente.",
      exCard3Title: "Trabajo Agendado",
      exCard3Text: "Ve el trabajo próximo por fecha, equipo, ubicación o empleado asignado.",
      exCard4Title: "Trabajos Activos",
      exCard4Text:
        "Monitorea el trabajo en curso e identifica trabajos que llevan activos más tiempo de lo esperado.",
      exCard5Title: "Trabajo Completado",
      exCard5Text:
        "Revisa trabajos, proyectos o solicitudes completadas durante el periodo seleccionado.",
      exCard6Title: "Requiere Atención",
      exCard6Text:
        "Muestra seguimientos vencidos, documentos faltantes, solicitudes sin asignar, trabajo detenido o errores de datos.",
      exStatusTitle: "Trabajo por estado",
      exWorkloadTitle: "Carga de trabajo por equipo o empleado",
      exWorkloadText:
        "Compara el trabajo asignado e identifica distribución desigual o personas sobrecargadas.",
      exActivityTitle: "Actividad en el tiempo",
      exActivityText:
        "Trabajo nuevo, agendado, completado o cerrado por días, semanas o meses.",
      exFollowUpsTitle: "Seguimientos pendientes",
      exFollowUpsText:
        "Registros que requieren acción del cliente, empleado, contratista o gerente.",
      exFilterTitle: "Filtro por ubicación o línea de servicio",
      exFilterText:
        "Enfoca el panel en una oficina, territorio, equipo o tipo de servicio específico.",
      exActivityFeedTitle: "Actividad reciente",
      exActivityFeedText:
        "Cambios de estado importantes, trabajo completado, nuevas asignaciones y excepciones.",
      exFollowUp1: "Presupuesto esperando respuesta del cliente",
      exFollowUp2: "Faltan fotos del sitio antes de agendar",
      exFollowUp3: "Solicitud sin asignar del formulario web",
      exFollowUp4: "Trabajo en pausa esperando aprobación del cliente",
      exActivity1: "Trabajo pasó a En Progreso",
      exActivity2: "Presupuesto entregado al cliente",
      exActivity3: "Nueva solicitud recibida del formulario de captación",
      exActivity4: "Trabajo marcado como completado por el equipo asignado",
      exTeam1: "Equipo A",
      exTeam2: "Equipo B",
      exTeam3: "Contratistas",
      exTeam4: "Sin asignar",
      exampleClosing:
        "Las métricas exactas, sus definiciones y la frecuencia de actualización dependen de los sistemas y la información disponible en el negocio.",

      rolesEyebrow: "Reportes por responsabilidad",
      rolesTitle: "No todos necesitan el mismo panel",
      rolesText1:
        "Dueños, gerentes, empleados y contratistas suelen necesitar distintos niveles de información.",
      rolesText2:
        "Kranbos puede organizar paneles y reportes según las responsabilidades de cada rol, en vez de mostrar a todos la misma pantalla abrumadora.",
      role1Title: "Vista del dueño",
      role1Text:
        "Ve la actividad de todo el negocio, tendencias, carga de trabajo, excepciones importantes y las áreas que requieren atención gerencial.",
      role2Title: "Gerente de operaciones",
      role2Text:
        "Monitorea asignaciones, trabajo activo, fechas límite, registros incompletos, capacidad del equipo y cuellos de botella.",
      role3Title: "Miembro del equipo",
      role3Text:
        "Ve el trabajo asignado, prioridades, fechas de entrega, información faltante y las acciones que siguen.",
      role4Title: "Contratista externo",
      role4Text:
        "Ve únicamente las asignaciones aprobadas, los documentos relevantes, las fechas y los requisitos de finalización.",
      rolesNote:
        "El acceso y la visibilidad deben basarse en lo que cada persona necesita entender o completar—no simplemente en los datos que existen.",

      deliveryEyebrow: "Más allá del panel",
      deliveryTitle: "Pon la información correcta frente a cada persona en el momento correcto",
      deliveryText1:
        "Algunas personas necesitan un panel interactivo. Otras necesitan un resumen semanal, un reporte de excepciones, un correo programado o un archivo descargable.",
      deliveryText2:
        "Kranbos ayuda a determinar el formato de reporte más útil para cada audiencia.",
      delivery1Title: "Paneles interactivos",
      delivery1Text: "Explora la actividad actual con filtros, vistas por estado y detalle.",
      delivery2Title: "Resúmenes semanales",
      delivery2Text:
        "Da a dueños y gerentes una visión consistente de lo que cambió durante la semana.",
      delivery3Title: "Reportes diarios de atención",
      delivery3Text:
        "Resalta pendientes vencidos, incompletos, fallidos o sin asignar que requieren acción.",
      delivery4Title: "Reportes gerenciales",
      delivery4Text:
        "Resume desempeño, carga de trabajo, avance y temas operativos de un periodo seleccionado.",
      delivery5Title: "Reportes por equipo",
      delivery5Text:
        "Da a departamentos, ubicaciones o empleados una vista enfocada de sus responsabilidades y resultados.",
      delivery6Title: "Reportes descargables",
      delivery6Text:
        "Genera exportaciones o archivos aprobados para reuniones, conciliación, análisis o registro.",
      deliveryNote:
        "Los formatos disponibles dependen de los sistemas involucrados y del enfoque de reportes elegido para tu negocio.",

      qualityEyebrow: "Reportes que puedes entender",
      qualityTitle: "Los reportes claros empiezan con definiciones claras",
      qualityText1: "Dos reportes pueden mostrar la misma etiqueta y calcularla de forma distinta.",
      qualityText2:
        "Por ejemplo, “cliente nuevo”, “trabajo completado”, “solicitud activa” o “conversión” pueden significar cosas distintas para distintos empleados o sistemas.",
      qualityText3:
        "Antes de construir un panel, Kranbos ayuda a definir qué significan las métricas importantes, de dónde viene la información de origen y qué registros deben incluirse.",
      quality1Title: "Definición de métricas",
      quality1Text: "Documenta cómo debe calcularse cada número, estado o categoría importante.",
      quality2Title: "Visibilidad del origen",
      quality2Text:
        "Deja claro qué plataforma, archivo o registro aporta la información de base.",
      quality3Title: "Validación de datos",
      quality3Text:
        "Identifica valores faltantes, duplicados, registros que no coinciden y uso inconsistente de estados que afectan los reportes.",
      quality4Title: "Expectativas de actualización",
      quality4Text:
        "Aclara si la información se actualiza de inmediato, de forma programada, después de una importación o tras una revisión manual.",
      qualityNote:
        "Un panel no debe generar confianza falsa. Si los datos disponibles están incompletos o son inconsistentes, la solución debe hacer visible esa limitación.",

      automationEyebrow: "Visibilidad + acción",
      automationTitle: "El panel muestra el problema. La automatización ayuda a resolverlo.",
      automationText1:
        "Los paneles y la automatización de flujos suelen funcionar en conjunto.",
      automationText2:
        "El panel da a dueños y empleados visibilidad de lo que está pasando. La automatización puede encargarse de acciones repetitivas como enviar recordatorios, crear tareas, solicitar información faltante, actualizar registros o escalar trabajo vencido.",
      automationColLeft: "Paneles y Reportes",
      automationLeft1: "Muestran el estado actual",
      automationLeft2: "Siguen actividad y tendencias",
      automationLeft3: "Resaltan cuellos de botella",
      automationLeft4: "Exponen excepciones",
      automationLeft5: "Comparan equipos o ubicaciones",
      automationLeft6: "Apoyan decisiones gerenciales",
      automationLeft7: "Entregan resúmenes programados",
      automationColRight: "Automatización de Flujos",
      automationRight1: "Mueve información entre sistemas",
      automationRight2: "Crea tareas y registros",
      automationRight3: "Envía notificaciones",
      automationRight4: "Solicita información faltante",
      automationRight5: "Actualiza estados aprobados",
      automationRight6: "Escala trabajo vencido",
      automationRight7: "Dispara los siguientes pasos",
      automationClosing:
        "Kranbos puede construir los reportes por separado o combinarlos con automatización cuando el negocio necesita visibilidad y acción.",
      automationCta: "Explorar Automatización de Flujos",

      toolsEyebrow: "Reportes dentro del flujo de trabajo",
      toolsTitle: "A veces el panel debe ser parte de la herramienta que usa tu equipo",
      toolsText1: "Un panel independiente es útil cuando la gente sobre todo necesita visibilidad.",
      toolsText2:
        "Cuando los empleados además necesitan asignar trabajo, actualizar registros, aprobar solicitudes, subir documentos o resolver excepciones, los reportes pueden ser más útiles dentro de una herramienta interna a medida.",
      toolsColLeft: "Un panel puede ser suficiente cuando:",
      toolsLeft1: "Los usuarios sobre todo necesitan visibilidad",
      toolsLeft2: "Los sistemas de origen ya permiten las acciones necesarias",
      toolsLeft3: "Los reportes se usan para monitoreo y decisiones",
      toolsLeft4: "El negocio necesita resúmenes recurrentes",
      toolsLeft5: "Los empleados pueden actuar en los sistemas originales",
      toolsColRight: "Una herramienta interna puede ser más adecuada cuando:",
      toolsRight1: "Los usuarios necesitan actualizar o gestionar registros",
      toolsRight2: "El trabajo debe asignarse o aprobarse",
      toolsRight3: "Los empleados necesitan una sola cola operativa",
      toolsRight4: "Los contratistas necesitan acceso controlado a tareas",
      toolsRight5: "Las excepciones deben resolverse en la misma interfaz",
      toolsRight6: "Los sistemas actuales no ofrecen un espacio de trabajo efectivo",
      toolsClosing:
        "Kranbos recomienda la solución más pequeña y práctica que resuelva el problema operativo real.",
      toolsCta: "Explorar Herramientas Internas",

      processEyebrow: "Cómo funciona",
      processTitle: "Empezamos por las decisiones detrás del reporte",
      process1Title: "Descubrimiento de reportes",
      process1Text:
        "Identificamos quiénes usan el reporte, qué preguntas necesitan responder y qué decisiones debe apoyar la información.",
      process2Title: "Revisión de fuentes de datos",
      process2Text:
        "Evaluamos los sistemas, hojas de cálculo, archivos, campos, estados y registros que contienen la información requerida.",
      process3Title: "Definición de métricas y reportes",
      process3Text:
        "Definimos indicadores clave, filtros, cálculos, expectativas de actualización y responsabilidades de reporte.",
      process4Title: "Desarrollo del panel",
      process4Text:
        "Construimos y probamos el panel, la lógica de reportes, las conexiones de datos, los filtros, las reglas de acceso y los métodos de entrega.",
      process5Title: "Lanzamiento y ajustes",
      process5Text:
        "Implementamos la solución, documentamos su uso, validamos los resultados y la mejoramos según la retroalimentación operativa real.",

      outcomesEyebrow: "El resultado",
      outcomesTitle: "Menos tiempo recopilando información. Más claridad sobre qué atender.",
      outcome1Title: "Una vista más clara",
      outcome1Text:
        "Reúne la información operativa más importante en una experiencia de reportes más organizada.",
      outcome2Title: "Reportes más consistentes",
      outcome2Text:
        "Reduce las diferencias en cómo los empleados calculan, categorizan y presentan la misma información.",
      outcome3Title: "Detección más rápida de problemas",
      outcome3Text:
        "Hace más fácil identificar actividad incompleta, vencida, detenida o inusual.",
      outcome4Title: "Mejor visibilidad gerencial",
      outcome4Text:
        "Ayuda a dueños y gerentes a entender carga de trabajo, avance, responsabilidades y cuellos de botella.",

      relatedTitle: "Un reporte confiable puede requerir más que un panel",
      relatedIntro:
        "Kranbos puede combinar reportes, automatización de flujos, herramientas internas y mejoras a hojas de cálculo en una sola solución operativa práctica.",
      relatedCurrentLabel: "Estás aquí",
      related1Title: "Paneles y Reportes",
      related1Text:
        "Convierte la información operativa en paneles, reportes y vistas de atención claras.",
      related2Title: "Automatización de Flujos",
      related2Text: "Conecta sistemas y mueve información y acciones automáticamente.",
      related3Title: "Herramientas Internas",
      related3Text:
        "Da a tu equipo una interfaz enfocada para gestionar y completar el trabajo operativo.",
      related4Title: "Rescate de Hojas de Cálculo",
      related4Text:
        "Mejora, automatiza o reemplaza reportes basados en hojas de cálculo que se volvieron difíciles de mantener o de confiar.",
      relatedLearnMore: "Saber más",

      finalCtaTitle: "Obtén una vista más clara de cómo está operando tu negocio",
      finalCtaText:
        "Cuéntanos qué mides hoy, dónde vive la información y qué te resulta difícil de entender. Te ayudaremos a determinar la forma más práctica de mejorar tus paneles y reportes.",
      finalPrimaryCta: "Solicitar una Auditoría de Reportes",
      finalSecondaryCta: "Contáctanos",
    },
  },
};
